import React from 'react';
import { FileText, DollarSign, ArrowUpRight, ArrowDownLeft, Calendar, CheckSquare, Edit2, Plus, Trash2, AlertCircle, Trophy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useRecentActivity } from '../../hooks/useRecentActivity';
import { UpcomingEventsCard } from './UpcomingEventsCard';
import { TaskSummary } from './TaskSummary';
import { useCalendar } from '../../hooks/useCalendar';
import { HabitTrackerCard } from './HabitTrackerCard';
import { useTaskContext } from '../../contexts/TaskContext';
import { useProfile } from '../../contexts/ProfileContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import type { Activity } from '../../types';

interface RecentActivityProps {
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
  tasks: Task[];
  onDeleteActivity?: (activity: Activity) => void;
}

export function RecentActivity({ onAddTask, onEditTask, tasks, onDeleteActivity }: RecentActivityProps) {
  const { activities, loading, error } = useRecentActivity();
  const navigate = useNavigate();
  const { events } = useCalendar();
  const { roles } = useTaskContext();
  const { profile } = useProfile();
  const { hasFeature } = useSubscription();
  const isPremium = hasFeature('custom_roles');
  
  const filteredActivities = activities.filter(activity => {
    if (activity.type !== 'task') return true;
    const taskRole = (activity as any).role;
    return !tasks.find(t => t.id === activity.id)?.archived && 
           (taskRole === undefined || tasks.find(t => t.id === activity.id)?.role === taskRole);
  });

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-xl flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <p className="text-sm">Failed to load recent activity. Please try refreshing the page.</p>
      </div>
    );
  }

  const todayEvents = events.filter(event => {
    const today = new Date();
    const eventDate = new Date(event.startTime);
    return (
      eventDate.getDate() === today.getDate() &&
      eventDate.getMonth() === today.getMonth() &&
      eventDate.getFullYear() === today.getFullYear()
    );
  });

  const handleEdit = (activity: Activity) => {
    if (activity.type === 'task') {
      onEditTask(activity);
    } else if (activity.type === 'brain-dump') {
      navigate(`/brain-dump/${activity.id}`);
    } else if (activity.type === 'invoice' || activity.type === 'expense') {
      navigate(`/business/transactions/${activity.id}`);
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'brain-dump':
        return <FileText className="w-4 h-4" />;
      case 'invoice':
        return <ArrowUpRight className="w-4 h-4" />;
      case 'expense':
        return <ArrowDownLeft className="w-4 h-4" />;
      case 'task':
        return <CheckSquare className="w-4 h-4 text-black" />;
      case 'challenge':
        return <Trophy className="w-4 h-4" />;
    }
  };

  const getActivityColor = (activity: Activity) => {
    if (activity.type === 'task') {
      return activity.priority === 'high' ? 'bg-[#bbdde5]' : 'bg-[#141414]';
    } else if (activity.type === 'challenge') {
      return 'bg-[#feeaa0]';
    }
    return 'bg-[#2A2B2E]';
  };

  const getActivityTextColor = (activity: Activity) => {
    return (activity.type === 'task' || activity.type === 'challenge')
      ? 'text-black'
      : 'text-white';
  };

  const getPriorityBadgeColor = (activity: Activity) => {
    if (activity.type === 'task') {
      return 'bg-black text-white';
    }
    return 'bg-gray-500 text-white';
  };

  return (
    <div className="space-y-1">
      <TaskSummary tasks={tasks} />
      
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl flex items-center gap-2 mt-4">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">
            {error instanceof Error ? error.message : 'Failed to load recent activity'}
          </p>
        </div>
      )}
      
      <div className="-mt-1">
        <UpcomingEventsCard />
      </div>
      
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">Failed to load recent activity. Please try refreshing the page. {error.message}</p>
        </div>
      )}
      
      <div className="grid gap-1">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent-color)] border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-400">Loading activities...</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No recent activities</div>
        ) : filteredActivities.map((activity) => (
          <div key={activity.id}>
            {activity.type === 'task' && activity.priority === 'high' ? (
              <HighPriorityTask
                activity={activity}
                onEdit={handleEdit}
                formatTimeAgo={formatTimeAgo}
              />
            ) : (
              <div className={`${activity.type === 'challenge' ? 'py-4 px-6' : 'p-6'} rounded-xl ${getActivityColor(activity)} cursor-pointer hover:opacity-90 transition-opacity shadow-lg`}>
                <div className="flex flex-col">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-1">
                      <div className={`flex-1 flex items-center gap-2 text-sm ${
                        'text-white'
                      }`}>
                        {activity.type !== 'challenge' && (
                          <>
                            <Calendar className="w-4 h-4 text-white" />
                            <div className="flex items-center gap-2">
                              <span>{formatTimeAgo(activity.timestamp)}</span>
                              {activity.type === 'task' && (
                                <span className="px-3 py-1 rounded-full text-sm bg-white/10 text-white">
                                  {isPremium 
                                    ? roles.find(r => r.id === activity.role)?.name || activity.role
                                    : activity.role.charAt(0).toUpperCase() + activity.role.slice(1) 
                                  }
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        {activity.type === 'news' && (
                          <span className="px-2 py-0.5 bg-black/5 rounded-full text-sm text-gray-500">News</span>
                        )}
                        {activity.type === 'brain-dump' && (
                          <span className="px-2 py-0.5 bg-white/10 rounded-full text-sm text-white/60">Brain Dump</span>
                        )}
                        {(activity.type === 'invoice' || activity.type === 'expense') && (
                          <span className="px-2 py-0.5 bg-white/10 rounded-full text-sm text-white/60 capitalize">{activity.type}</span>
                        )}
                        {activity.type === 'challenge' && (
                          <h3 className="text-sm font-extrabold text-black">
                            {activity.title}
                          </h3>
                        )}
                      </div>
                      {activity.type === 'challenge' ? (
                        <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                          <Link 
                            to={`/challenges`} 
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center justify-center hover:opacity-80 transition-opacity"
                          >
                            <ArrowUpRight className="w-4 h-4 text-white" />
                          </Link>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(activity)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity ${
                            activity.type === 'task' && activity.priority === 'high'
                              ? 'bg-black/10'
                              : activity.type === 'task'
                                ? 'bg-black/10'
                                : 'bg-white/10'
                          }`}
                        >
                          <Edit2 className={`w-6 h-6 ${
                            activity.type === 'task'
                              ? 'text-white' 
                              : 'text-white'
                          }`} />
                        </button>
                      )}
                    </div>
                    {activity.amount && (
                      <span className={`font-medium ${
                        activity.type === 'invoice' || activity.type === 'expense' ? 'text-white' : ''
                      }`}>
                        {activity.type === 'invoice' ? '+' : '-'}${activity.amount}
                      </span>
                    )}
                    {activity.type === 'news' && activity.imageUrl && (
                      <div className="relative w-[calc(100%+1.5rem)] -mx-3 h-48 mb-3 overflow-hidden bg-black/5">
                        <img
                          src={activity.imageUrl}
                          alt={activity.title} 
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.currentTarget.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
                          }}
                        />
                      </div>
                    )}
                    {activity.type !== 'challenge' && (
                      <h3 className={`text-sm font-bold ${
                        'text-white'
                      }`}>
                        {activity.title}
                      </h3>
                    )}
                    {activity.type === 'news' && activity.content && (
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {activity.content}
                      </p>
                    )}
                    {activity.type === 'task' && (
                      <>
                        <div className="flex justify-end mt-2">
                          {onDeleteActivity && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteActivity(activity);
                              }}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity bg-black/10 text-white"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activity.id === activities[0]?.id && (
              <div className="grid gap-2 mt-2">
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}